import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import pricingPlanTemplateUrl from "/pricing_plan_template.csv?url";
import pricingPlanTemplateWithExamplesUrl from "/pricing_plan_template_with_examples.csv?url";
import { useAppSelector } from "../../../redux/hooks";
import FileDropZone from "../../../components/core/blocks/FileDropZone";
import { uploadToS3 } from "../../uploads/uploadToS3";
import { apiClient } from "../../../zodios/client";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";

export function UploadStep({ onUploadCreated }: { onUploadCreated: (uploadId: number) => void }) {
  const queryClient = useQueryClient();
  const { t } = useTranslation(["pricingv2"]);
  const { marketPlace } = useAppSelector((state) => state.marketPlace);

  const uploadMutation = useMutation({
    mutationFn: async ({ file }: { file: File }) => {
      const uploadId = await uploadToS3({ file, uploadType: "pricing_plan" });
      await apiClient.processUpload({ marketplace: marketPlace }, { params: { id: uploadId } });
      void queryClient.invalidateQueries({ queryKey: [{ entity: "uploads" }] });
      return uploadId;
    },
    onSuccess: (uploadId) => onUploadCreated(uploadId),
  });

  return (
    <VStack gap="medium" alignItems="stretch">
      <HStack gap="small" alignItems="center" className={css({ fontSize: "small" })}>
        <span className={css({ color: "gray.600" })}>{t(($) => $.pricingv2["Download a template:"])}</span>
        <a href={pricingPlanTemplateUrl} download>
          {t(($) => $.pricingv2["Clean Template"])}
        </a>
        <span className={css({ color: "gray.400" })}>•</span>
        <a href={pricingPlanTemplateWithExamplesUrl} download>
          {t(($) => $.pricingv2["With Examples"])}
        </a>
      </HStack>
      <FileDropZone
        onFileSelect={(file: File) => uploadMutation.mutate({ file })}
        disabled={uploadMutation.isPending}
        label={t(($) => $.pricingv2["Drop the file to be imported here, or click the button below"])}
        isPending={uploadMutation.isPending}
        acceptedFileTypes={[".csv"]}
      />
    </VStack>
  );
}
